import { useState } from 'react';

/**
 * O editor do território de distribuição (ADR-128).
 *
 * <p>UF por UF, com as regiões como atalho — região não é código a mais, é um jeito rápido de
 * marcar várias. "Brasil inteiro" desmarca as UFs, porque o servidor as absorveria de qualquer
 * jeito. Outros países vão por sigla ISO, separados por vírgula.
 */
export const REGIOES: { nome: string; ufs: string[] }[] = [
  { nome: 'Norte', ufs: ['AC', 'AM', 'AP', 'PA', 'RO', 'RR', 'TO'] },
  { nome: 'Nordeste', ufs: ['AL', 'BA', 'CE', 'MA', 'PB', 'PE', 'PI', 'RN', 'SE'] },
  { nome: 'Centro-Oeste', ufs: ['DF', 'GO', 'MS', 'MT'] },
  { nome: 'Sudeste', ufs: ['ES', 'MG', 'RJ', 'SP'] },
  { nome: 'Sul', ufs: ['PR', 'RS', 'SC'] },
];

export function EditorDeTerritorio({
  inicial,
  ocupado,
  aoSalvar,
  aoCancelar,
  rotuloSalvar = 'Salvar território',
}: {
  inicial: string[] | null | undefined;
  ocupado: boolean;
  aoSalvar: (codes: string[]) => void;
  aoCancelar?: () => void;
  rotuloSalvar?: string;
}) {
  const [brasil, setBrasil] = useState((inicial ?? []).includes('BR'));
  const [ufs, setUfs] = useState<Set<string>>(
    new Set((inicial ?? []).filter((c) => c.startsWith('BR-')).map((c) => c.slice(3))),
  );
  const [paises, setPaises] = useState(
    (inicial ?? []).filter((c) => c.length === 2 && c !== 'BR').join(', '),
  );

  function alternar(uf: string) {
    const novo = new Set(ufs);
    if (novo.has(uf)) {
      novo.delete(uf);
    } else {
      novo.add(uf);
    }
    setUfs(novo);
  }

  function regiao(lista: string[]) {
    const todas = lista.every((uf) => ufs.has(uf));
    const novo = new Set(ufs);
    lista.forEach((uf) => (todas ? novo.delete(uf) : novo.add(uf)));
    setUfs(novo);
  }

  function codigos(): string[] {
    const saida: string[] = brasil ? ['BR'] : [...ufs].map((uf) => `BR-${uf}`);
    paises
      .split(/[,\s]+/)
      .map((p) => p.trim().toUpperCase())
      .filter((p) => p.length > 0)
      .forEach((p) => saida.push(p));
    return saida;
  }

  return (
    <div>
      <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontWeight: 600 }}>
        <input
          type="checkbox"
          style={{ width: 16, height: 16, margin: 0 }}
          checked={brasil}
          onChange={(e) => setBrasil(e.target.checked)}
        />
        Brasil inteiro
      </label>

      {!brasil && (
        <div style={{ marginTop: 10, display: 'grid', gap: 8 }}>
          {REGIOES.map((r) => (
            <div key={r.nome} style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
              <button
                type="button"
                className="btn btn-ghost"
                style={{ minWidth: 110, justifyContent: 'flex-start' }}
                onClick={() => regiao(r.ufs)}
              >
                {r.nome}
              </button>
              {r.ufs.map((uf) => (
                <label
                  key={uf}
                  className={ufs.has(uf) ? 'chip chip-teal' : 'chip chip-neutral'}
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                >
                  <input
                    type="checkbox"
                    checked={ufs.has(uf)}
                    onChange={() => alternar(uf)}
                    style={{ display: 'none' }}
                  />
                  {uf}
                </label>
              ))}
            </div>
          ))}
        </div>
      )}

      <div className="campo" style={{ marginTop: 12 }}>
        <label htmlFor="paises">Outros países (sigla ISO, separados por vírgula)</label>
        <input
          id="paises"
          value={paises}
          onChange={(e) => setPaises(e.target.value)}
          placeholder="PT, AR"
        />
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button className="btn btn-primary" disabled={ocupado} onClick={() => aoSalvar(codigos())}>
          {ocupado ? 'Salvando…' : rotuloSalvar}
        </button>
        {aoCancelar && (
          <button className="btn btn-ghost" disabled={ocupado} onClick={aoCancelar}>
            Cancelar
          </button>
        )}
      </div>
    </div>
  );
}

/** "SP, RJ, MG" / "Brasil inteiro" / "+ PT" — como a lista se lê numa linha. */
export function territorioEmTexto(codes: string[] | null | undefined): string {
  if (!codes || codes.length === 0) {
    return 'não declarado';
  }
  const partes: string[] = [];
  if (codes.includes('BR')) {
    partes.push('Brasil inteiro');
  } else {
    const ufs = codes.filter((c) => c.startsWith('BR-')).map((c) => c.slice(3));
    if (ufs.length > 0) {
      partes.push(ufs.join(', '));
    }
  }
  codes.filter((c) => c.length === 2 && c !== 'BR').forEach((c) => partes.push(c));
  return partes.join(' · ');
}
